import React, {useCallback, useEffect, useRef} from 'react';
import {ControlPosition, MapControl, Marker, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import MapButtonsControl from "../../../components/map-report/map-buttons-control.jsx";
import Button from "../../../components/button/button.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import {FaLocationDot} from "react-icons/fa6";
import {FaSearch, FaTrash} from "react-icons/fa";
import {mapStyles} from "../../../utils/map-styles.js";
import projectUtils from "../../../utils/project-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";

const API_URL = import.meta.env.VITE_API_URL;

const MapComponent = ({bounds, zoom, isPclSelection, pclMarkers, markersSelected, search, setSearch, setBounds, setZoom, latitude, longitude,
                       address, addressSearch, geocodeAddress, handleAddMarker, setCurrentPosition, setMarkers, mapState, setMapState, itemClicked,
                       position, handleRemoveAll, sendToSentryView, handleRemoveSelectedMarker, setDoubleClickEvent, setAddressSearch, handleMapClick}) => {

    const map = useMap();

    const drawingLibrary = useMapsLibrary("drawing");
    const isDrawing = useRef(false);
    const rectangleRef = useRef(null);
    const startPoint = useRef(null);
    const isShiftPressed = useRef(false);
    const initializedBounds = useRef(false);

    const hasValidCoordinates = (lat, lng) => {
        if (lat === null || lat === undefined || lng === null || lng === undefined) return false;

        const latitude = Number(lat);
        const longitude = Number(lng);

        return Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 0 && longitude === 0);
    };

    const centralizeOnMarkers = () => {
        if (!map || !window.google) return;

        if (markersSelected.length === 0) {
            const bounds = new google.maps.LatLngBounds();

            pclMarkers.filter(marker => hasValidCoordinates(marker.latitude, marker.longitude)).forEach(marker => {
                bounds.extend({
                    lat: Number(marker.latitude),
                    lng: Number(marker.longitude)
                });
            });

            if (!bounds.isEmpty()) setBounds(bounds);
            return;
        }

        if (markersSelected.length === 1) {
            const marker = markersSelected[0];
            const lat = marker.lat ?? marker.latitude;
            const lng = marker.lng ?? marker.longitude;

            if (!hasValidCoordinates(lat, lng)) return;

            map.setCenter({
                lat: Number(lat),
                lng: Number(lng)
            });

            map.setZoom(18);
            return;
        }

        const bounds = new google.maps.LatLngBounds();

        markersSelected.filter(marker => hasValidCoordinates(marker.lat ?? marker.latitude, marker.lng ?? marker.longitude)).forEach(marker => {
            bounds.extend({
                lat: Number(marker.lat ?? marker.latitude),
                lng: Number(marker.lng ?? marker.longitude)
            });
        });

        if (!bounds.isEmpty()) setBounds(bounds);
    };

    const moveToPosition = (position, zoomLevel) => {
        if (!map) return;
        if (!position || !Number.isFinite(Number(position.lat)) || !Number.isFinite(Number(position.lng))) return;

        map.setCenter({
            lat: Number(position.lat),
            lng: Number(position.lng)
        });

        if (Number.isFinite(Number(zoomLevel))) {
            map.setZoom(Number(zoomLevel));
        }
    };

    const geocode = (address) => {
        const geocoder = new window.google.maps.Geocoder();

        return new Promise((resolve, reject) => {
            geocoder.geocode(
                { address },
                (results, status) => {
                    if (status === "OK") {
                        resolve(results);
                    } else {
                        reject(new Error(status));
                    }
                }
            );
        });
    };

    const handleSearchButton = async () => {
        try {
            const results = await geocode(addressSearch);

            map.setZoom(18);
            map.setCenter(results[0].geometry.location);

            handleAddMarker({
                description: results[0].formatted_address,
                address: results[0].address_components,
                latitude: Number(results[0].geometry.location.lat()),
                longitude: Number(results[0].geometry.location.lng()),
            }, "user");

        } catch (error) {
            projectUtils.swalMessage("error", "", "Endereço não encontrado.", "Ok");
            console.error(error);
        }
    };

    const handleDragEnd = (event) => {
        if (isPclSelection) return;

        const latitude = Number(event?.latLng.lat())
        const longitude = Number(event?.latLng.lng())

        handleAddMarker({
            latitude: latitude,
            longitude: longitude,
        }, "user")

        setCurrentPosition({
            lat: Number(event?.latLng.lat()),
            lng: Number(event?.latLng.lng()),
        })

        setZoom(map.getZoom());

    }

    const handleInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSearchButton();
        }
    }

    const clear = useCallback(() => {
        setSearch("");
        setAddressSearch("");
    }, [])

    useGlobalKey("Escape", clear);

    useEffect(() => {

        const addInitialMarker = async () => {
            if (!isPclSelection && map) {

                let lat = latitude
                let lng = longitude

                if (address){
                    try {
                        const coords = await geocodeAddress(address);

                        if (coords) {
                            lat = coords.lat;
                            lng = coords.lng;
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }

                handleAddMarker({
                    type: "user",
                    latitude: Number(lat),
                    longitude: Number(lng)
                }, "user")

                setCurrentPosition({lat: Number(lat), lng: Number(lng)})
            }
        };

        addInitialMarker();
    }, [latitude, longitude, map]);

    useEffect(() => {
        if (!map || !drawingLibrary || !isPclSelection) return;

        const handleKeyDown = (event) => {
            if (event.key === "Shift") {
                isShiftPressed.current = true;
            }
        };

        const handleKeyUp = (event) => {
            if (event.key === "Shift") {
                isShiftPressed.current = false;
            }
        };

        const handleMouseDown = (event) => {

            if (isDrawing.current || !isShiftPressed.current || !event.latLng) return;
            isDrawing.current = true;
            startPoint.current = event.latLng;

            if (rectangleRef.current) rectangleRef.current.setMap(null);

            rectangleRef.current = new google.maps.Rectangle({
                bounds: new google.maps.LatLngBounds(startPoint.current, startPoint.current),
                editable: false,
                draggable: false,
                strokeColor: "rgb(74,74,74)",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "rgb(165,165,165)",
                fillOpacity: 0.2,
                map,
            });

            map.setOptions({ draggable: false, gestureHandling: "none" });
            rectangleRef.current.addListener("mouseup", handleMouseUp);
        };

        const mouseUpListener = map.addListener("mouseup", () => {
            handleMouseUp();
        });

        const handleMouseMove = (event) => {
            if (!isDrawing.current || !rectangleRef.current || !isShiftPressed.current) return;

            const bounds = new google.maps.LatLngBounds(startPoint.current);
            bounds.extend(event.latLng);
            rectangleRef.current.setBounds(bounds);
        };

        const handleMouseUp = () => {
            if (!isDrawing.current || !rectangleRef.current || !isShiftPressed.current) return;
            isDrawing.current = false;

            const bounds = rectangleRef.current.getBounds();
            rectangleRef.current.setMap(null);
            rectangleRef.current = null;
            if (!bounds) return;

            map.setOptions({ draggable: true, gestureHandling: "greedy" });

            const selectedMarkers = [];

            pclMarkers.forEach((marker) => {

                if (bounds.contains({lat: Number(marker.latitude), lng: Number(marker.longitude)})) {
                    selectedMarkers.push({
                        ...marker,
                        type: "pcl"
                    });
                }
            });

            setMarkers(selectedMarkers);
        };

        map.addListener("mousedown", handleMouseDown);
        map.addListener("mousemove", handleMouseMove);

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("keyup", handleKeyUp);

        return () => {
            google.maps.event.clearListeners(map, "mousedown");
            google.maps.event.clearListeners(map, "mousemove");
            google.maps.event.removeListener(mouseUpListener);

            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("keyup", handleKeyUp);
        };
    }, [map, drawingLibrary, pclMarkers, isPclSelection, setMarkers]);

    useEffect(() => {
        if (!map || !isPclSelection || pclMarkers.length === 0) return;

        if (initializedBounds.current) return;
        initializedBounds.current = true;

        const bounds = new google.maps.LatLngBounds();

        pclMarkers.filter(marker => hasValidCoordinates(marker.latitude, marker.longitude)).forEach(marker => {
            bounds.extend({
                lat: Number(marker.latitude),
                lng: Number(marker.longitude)
            });
        });

        map.fitBounds(bounds);

    }, [map, pclMarkers, isPclSelection]);

    useEffect(() => {
        if (!map) return;

        map.setOptions({
            styles: mapStyles({
                neighborhoodStreets: mapState.neighborhoodStreets,
                pointsOfInterest: mapState.pointsOfInterest
            })
        });
    }, [mapState, map]);

    useEffect(() => {
        if (!map) return;
        moveToPosition(position);
    }, [position, map]);


    useEffect(() => {
        if (map && !isPclSelection) {
            setTimeout(() => {
                map.setZoom(zoom);
            }, 200);
        }
    }, [map]);

    useEffect(() => {
         if (!map || !bounds) return;
         const div = map.getDiv();

         if (div && div.offsetHeight > 0){
             map.fitBounds(bounds, 60);
             return;
         }

         const observer = new ResizeObserver(() => {
             if (div.offsetHeight > 0){
                 map.fitBounds(bounds, 60);
                 observer.disconnect();
             }
         });

         observer.observe(div);
         return () => observer.disconnect();
    }, [map, bounds]);

    useEffect(() => {
        setZoom(map.getZoom());
    }, [handleMapClick])

    return (
        <>
            { isPclSelection && (
                <MapControl position={ControlPosition.RIGHT_TOP}>
                    <div className="flex flex-col text-center px-4 pt-4 pb-2 bg-white rounded-b-[5px] border border-[#e7e7e7] w-[380px] max-h-[610px] h-auto">

                        <h3 className="font-bold mt-1">Pontos de coleta</h3>

                        <div className="flex gap-2 text-center">
                            <InputForm
                                id="search"
                                label="Pesquisar"
                                value={search}
                                onChange={({target}) => setSearch(target.value)}
                                classNameWrapper={"w-full"}
                                autocomplete={'off'}
                                autofocus={true}
                                isClearable={true}
                            />

                            <Button target="primary-outline" onClick={centralizeOnMarkers} className={"flex-1"}>Centralizar</Button>
                        </div>

                        <div className="pt-3 w-full">
                            <div className="mt-3 border border-[#cfcfcf] rounded-[5px] overflow-auto h-[310px] max-h-[310px]">

                                <div className="my-4"><span className={'font-bold text-[16px]'}>Pontos selecionados</span></div>

                                { markersSelected.filter(item => item.description.toLowerCase().includes(search.toLowerCase())).map((marker, index, array) => (
                                    <div key={marker.description} title={marker.description}>
                                        <div className="flex items-center justify-between px-1">

                                            <div className={`flex items-center gap-2 min-w-0 flex-1 p-1 cursor-pointer!`} onClick={() => itemClicked(marker)}>
                                                <div className="w-7 h-7 flex items-center justify-center shrink-0">

                                                    { marker.type === "pcl" ? (
                                                        <img src={`${API_URL}/public/assets/img/markers-gis/capturePlace/blue.svg`} alt="capturePlace" className={'transform translate-y-1'}/>
                                                    ) : (
                                                        <FaLocationDot color={'#e94338'}/>
                                                    )}

                                                </div>

                                                <span className="truncate" style={{ maxWidth: "235px" }}>
                                                    {marker.description}
                                                </span>

                                            </div>

                                            { marker.type === "pcl" && (
                                                <Button
                                                    className="h-[25px]! shrink-0"
                                                    target="danger"
                                                    title="Remover ponto"
                                                    onClick={() => handleRemoveSelectedMarker(marker.code)}
                                                >
                                                    <FaTrash size={13}/>
                                                </Button>
                                            )}

                                        </div>

                                        { index < array.length - 1 && (
                                            <hr className="w-[95%] mx-auto! border-0 border-t border-[#cfcfcf] opacity-[65%]"/>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className={"mb-2"}>
                                { markersSelected.length > 0 && (

                                    <div className="flex gap-2 mt-4 w-full">
                                        <Button target="danger" className={'w-full'} onClick={handleRemoveAll}>Remover todos</Button>
                                        <Button className={'w-full'} onClick={sendToSentryView}>Confirmar seleção</Button>
                                    </div>

                                )}

                            </div>
                        </div>
                    </div>
                </MapControl>
            )}

            { !isPclSelection && (
                <>
                    <MapControl position={ControlPosition.TOP_LEFT}>
                        <div className="px-2 pb-2 pt-4 w-[460px]">

                            <div className="flex flex-row gap-2">
                                <InputForm
                                    id="addressSearch"
                                    label="Pesquisar"
                                    value={addressSearch}
                                    onChange={({target}) => setAddressSearch(target.value)}
                                    onKeyDown={handleInputKeyDown}
                                    classNameWrapper={"w-full"}
                                    autocomplete={'off'}
                                    inputClass={'bg-white'}
                                    autofocus={true}
                                    isClearable={true}
                                />

                                <Button title="Aplicar" onClick={handleSearchButton} className={"w-fit"}><FaSearch size={13}/> </Button>
                            </div>
                        </div>
                    </MapControl>
                </>
            )}

            <MapButtonsControl
                mapState={mapState}
                onTogglePOI={() => setMapState(p => ({ ...p, pointsOfInterest: !p.pointsOfInterest }))}
                onToggleNeighborhood={() => setMapState(p => ({ ...p, neighborhoodStreets: !p.neighborhoodStreets }))}
            />

            { isPclSelection && (
                <MapControl position={ControlPosition.TOP_LEFT}>
                    <div className="map-tip m-3 ">
                        <div className="flex w-fit bg-[#ffa500] p-3 rounded-[3px] text-white !text-[15px] shadow">
                            <span className="me-2 select-none">Para selecionar: Mantenha 'Shift' pressionada e arraste com o mouse ou clique duas vezes no PCL.</span>
                        </div>
                    </div>
                </MapControl>
            )}

            { !isPclSelection && markersSelected.map((marker) => (
                <>
                    { marker.type === "user" && (
                        <Marker
                            key={`${marker.lat}-${marker.lng}`}
                            onDragEnd={handleDragEnd}
                            draggable={true}
                            position={{lat: Number(marker.lat), lng: Number(marker.lng)}}
                        />
                    )}
                </>
            ))}

            { isPclSelection && pclMarkers.map((marker) => {
                if (marker.mobile === true) return;

                return (
                    <Marker
                        key={marker.code}
                        title={marker.description}
                        ref={(e) => {setDoubleClickEvent(e, marker)}}
                        icon={{
                            url: `${API_URL}/public/assets/img/markers-gis/capturePlace/blue-zoom1.svg`,
                            scaledSize: new google.maps.Size(42, 42)
                        }}
                        position={{lat: Number(marker.latitude), lng: Number(marker.longitude)}}
                        animation={marker.bounce ? google.maps.Animation.BOUNCE : null}
                    />
                );
            })}
        </>
    )
}

export default MapComponent;